import type { Dictionary } from './types'

/**
 * `satisfies Dictionary` is the guard: adding a key to pt-BR without adding it
 * here is a compile error, and so is a typo in a key name.
 *
 * Football vocabulary follows British usage ("fixtures", "match", "round")
 * since that is what the competitions themselves use.
 */
export const en = {
  // ── Common ────────────────────────────────────────────────────────────────
  'common.cancel':   'Cancel',
  'common.save':     'Save',
  'common.close':    'Close',
  'common.back':     'Back',
  'common.retry':    'Try again',
  'common.confirm':  'Confirm',
  'common.copy':     'Copy',
  'common.copied':   'Copied',
  'common.loading':  'Loading…',

  // ── Navigation ────────────────────────────────────────────────────────────
  'nav.home':     'Home',
  'nav.survivor': 'Survivor',
  'nav.leagues':  'Leagues',
  'nav.profile':  'Profile',
  'nav.openMenu': 'Open menu',
  'nav.userMenu': 'User menu',
  'nav.plans':    'Plans',
  'nav.logout':   'Log out',
  'nav.createGame':   'Create game',
  'nav.joinWithCode': 'Join with a code',

  // ── Preferences ───────────────────────────────────────────────────────────
  'prefs.title':        'Preferences',
  'prefs.language':     'Language',
  'prefs.theme':        'Theme',
  'prefs.theme.system': 'System',
  'prefs.theme.light':  'Light',
  'prefs.theme.dark':   'Dark',
  'prefs.openLabel':    'Language and theme',
  'prefs.switchToDark':  'Switch to dark theme',
  'prefs.switchToLight': 'Switch to light theme',

  // ── Survivor · status ─────────────────────────────────────────────────────
  'survivor.status.alive':      'Alive',
  'survivor.status.eliminated': 'Out',
  'survivor.status.champion':   'Champion',
  'survivor.status.champions':  'Champions',
  'survivor.status.waiting':    'Not started',
  'survivor.status.inProgress': 'In progress',
  'survivor.status.finished':   'Finished',

  // ── Survivor · a pick's outcome ───────────────────────────────────────────
  'survivor.pick.won':     'Won',
  'survivor.pick.lost':    'Lost',
  'survivor.pick.live':    'Live',
  'survivor.pick.pending': 'Pending',

  // ── Survivor · the round ──────────────────────────────────────────────────
  'survivor.round':            'Round {number}',
  'survivor.round.short':      'Rd. {number}',
  'survivor.round.locked':     'Picks locked',
  'survivor.round.noCalendar': 'The competition calendar is not available yet.',

  // ── Survivor · picking ────────────────────────────────────────────────────
  'survivor.pick.heading':     'Pick your team',
  'survivor.pick.yours':       'Your pick',
  'survivor.pick.pendingCta':  'Pick pending',
  'survivor.pick.choose':      'Pick a team',
  'survivor.pick.missed':      'You did not pick this round',
  'survivor.pick.home':        'Home',
  'survivor.pick.away':        'Away',
  'survivor.pick.empty':       'No teams available this round.',
  'survivor.pick.othersTitle': 'Other players’ picks',
  'survivor.pick.selected':    'Picked',
  'survivor.pick.exhausted':   'No teams left — you have used every team playing this round.',
  'survivor.pick.closed':      'Picks for this round are closed.',
  'survivor.pick.currentOnly': 'You can only pick for the current round.',
  'survivor.pick.gameOver':    'Game over.',
  'survivor.pick.eliminatedBanner': 'You are out. Follow the remaining survivors.',
  'survivor.pick.yoursIs':     'Your pick: {team}',
  'survivor.pick.a11y.home':    'Pick {team} at home against {opponent}',
  'survivor.pick.a11y.away':    'Pick {team} away against {opponent}',
  'survivor.pick.a11y.neutral': 'Pick {team} against {opponent}',
  'survivor.pick.none':        '— no pick',
  'survivor.result.forfeit':     'No pick',
  'survivor.result.through':     'Through',
  'survivor.result.provisional': 'provisional',
  'survivor.history.title': 'Your picks',
  'survivor.history.empty': 'You have not picked in this game yet.',
  'survivor.peers.hiddenUntilLock': 'Everyone else’s picks appear once round {number} locks.',
  'survivor.peers.empty': 'No picks recorded for this round.',

  // ── Survivor · the game ───────────────────────────────────────────────────
  'survivor.game.state':      'Status',
  'survivor.game.copyCode':   'Copy code {code}',
  'survivor.game.code':       'Code',
  'survivor.game.aliveCount': 'Alive',
  'survivor.game.you':        'You',
  'survivor.game.notFound':   'Game unavailable',
  'survivor.game.rules':      'How Survivor works',
  'survivor.game.invite':     'Invite players',
  'survivor.game.settings':   'Settings',
  'survivor.game.title':         'Game',
  'survivor.game.notifications': 'Notifications',
  'survivor.game.moreOptions':   'More options',
  'survivor.game.editTeamName':  'Edit team name',
  'survivor.game.teamNameLocked': 'The team name cannot change once the game has started',
  'survivor.game.settingsMenu':  'Game settings',
  'survivor.game.leave':         'Leave game',
  'survivor.game.leaveConfirm':  'Confirm leaving?',
  'survivor.game.leaveBlocked':  'Hand over ownership before leaving',

  'survivor.list.error':            'Could not load your games. Try again in a moment.',
  'survivor.list.emptyTitle':       'You are not in any game yet',
  'survivor.list.emptyDescription': 'Create a game, or paste the invite code someone sent you.',
  'survivor.list.joinCode':         'Invite code',
  'survivor.list.codeFormat':       'Format: XXX-XXXX',
  'survivor.list.join':             'Join',

  // ── Survivor · standings ──────────────────────────────────────────────────
  'survivor.standings.title':        'Survivors',
  'survivor.standings.meta':         '{alive} alive · {out} out',
  'survivor.standings.alive':        'No one is still alive.',
  'survivor.standings.noEliminated': 'No one is out yet.',

  // Seasons / hall of fame
  'survivor.season.short':          'Season {number}',
  'survivor.season.restart':        'New season',
  'survivor.season.restartPrompt':  'The game is over. Start again with the same players — the invite code still works.',
  'survivor.season.restartConfirm': 'Sure? A new season starts with everyone alive again.',
  'survivor.season.restartConfirmCta': 'Confirm',
  'survivor.season.nextExists':     'Season {number} has already started.',
  'survivor.season.goToNext':       'Go to the current season',
  'survivor.hallOfFame.title':      'Hall of fame',
  'survivor.hallOfFame.inProgress': 'In play',
  'survivor.hallOfFame.noChampion': 'No champion',

  // ── Home ──────────────────────────────────────────────────────────────────
  'greeting.morning':   'Good morning',
  'greeting.afternoon': 'Good afternoon',
  'greeting.evening':   'Good evening',

  'home.gamesCount':  { one: '1 game',   other: '{count} games' },
  'home.activeCount': { one: '1 active', other: '{count} active' },
  'home.moreGames':   {
    one:   '+1 more game in this competition',
    other: '+{count} more games in this competition',
  },
  'home.myGames': 'My games',

  // ── Fixtures ──────────────────────────────────────────────────────────────
  'phase.groupStage': 'Group stage',
  'phase.r32':        'Round of 32',
  'phase.r16':        'Round of 16',
  'phase.qf':         'Quarter-finals',
  'phase.sf':         'Semi-finals',
  'phase.final':      'Final',

  'fixture.thisRound': 'This round',
  'fixture.results':   'Round {number} — results',
  'fixture.empty':     'No matches available.',
  'fixture.error':     'Could not load the fixtures.',
  'fixture.postponed': 'Postponed',
  'fixture.live':      'Live',
  'fixture.today':     'Today',
  'fixture.vs':        'vs',
  'fixture.group':     'Gr.{code}',
  'fixture.count':     { one: '1 match', other: '{count} matches' },

  // ── Errors ────────────────────────────────────────────────────────────────
  'error.generic':                    'Something went wrong. Try again.',
  'error.network':                    'Cannot reach the server.',
  'error.not_found':                  'Not found.',
  'error.forbidden':                  'You do not have permission to do that.',
  'error.validation_failed':          'Check the details and try again.',
  'error.internal_error':             'Server error. Try again in a moment.',
  'error.rate_limit_exceeded':        'Too many attempts. Wait a moment.',
  'error.invalid_invite_code':        'Invalid code.',
  'error.invite_code_not_found':      'Code not found.',
  'error.survivor_game_not_found':    'Game not found.',
  'error.survivor_already_member':    'You are already in this game.',
  'error.survivor_game_full':         'This game is full.',
  'error.survivor_game_locked':       'This game has already started.',
  'error.survivor_limit_reached':     'You have reached your game limit.',
  'error.team_already_picked':        'You have already used this team.',
  'error.team_not_playing_this_gameweek': 'This team does not play this round.',
  'error.team_not_in_competition':    'This team is not in this competition.',
  'error.pick_locked':                'Picks for this round are closed.',
  'error.participation_eliminated':   'You are already out of this game.',
  'error.not_participant':            'You are not in this game.',
  'error.not_game_owner':             'Only the game owner can do that.',
  'error.team_name_taken':            'That team name is already taken in this game.',
  'error.no_active_competition':      'No competition is running right now.',
  'error.survivor.load':              'Could not load this game.',
  'error.survivor.create':            'Could not create the game.',
  'error.survivor.restart':           'Could not start the new season.',
  'error.survivor_game_not_finished': 'This game has not finished yet.',
  'error.survivor_already_restarted': 'This game already has a newer season.',
  'error.survivor_no_rounds_left':    'The competition has no rounds left for a new season.',
} satisfies Dictionary
